import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { ShareButton } from '@/components/FollowShare';
import { Heart, TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * Followers widget for the artist's own dashboard.
 *
 * Shows total followers, this-week delta vs last-week, and a "Share my profile"
 * affordance to drive cross-platform funnel. Read-only — interactions happen
 * on the public profile page.
 */
const Trend = ({ now, prev }) => {
  if (now === prev) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
        <Minus className="h-3 w-3" /> Flat
      </span>
    );
  }
  if (now > prev) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
        <TrendingUp className="h-3 w-3" /> +{now - prev} vs last week
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-rose-300">
      <TrendingDown className="h-3 w-3" /> {now - prev} vs last week
    </span>
  );
};

const FollowersWidget = ({ artist }) => {
  const [stats, setStats] = useState(null);
  const load = useCallback(async () => {
    if (!artist?.artist_id) return;
    try {
      const { data } = await api.get(`/artists/${artist.artist_id}/social-stats`);
      setStats(data);
    } catch (_e) {
      setStats({ total: artist.follower_count || 0, this_week: 0, last_week: 0 });
    }
  }, [artist]);
  useEffect(() => { load(); }, [load]);

  if (!artist) return null;

  return (
    <div
      data-testid="dashboard-followers-widget"
      className="glass-strong rounded-2xl border border-white/10 p-6 mb-8 flex flex-col sm:flex-row sm:items-center gap-6"
    >
      <div className="flex-1">
        <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 mb-1.5 inline-flex items-center gap-1.5">
          <Heart className="h-3.5 w-3.5" /> Followers
        </div>
        <div className="flex items-baseline gap-3">
          <span data-testid="dashboard-followers-total" className="font-display text-4xl text-white font-bold">
            {stats ? stats.total.toLocaleString('en-IN') : '—'}
          </span>
          {stats && <Trend now={stats.this_week} prev={stats.last_week} />}
        </div>
        <p className="text-xs text-slate-400 mt-2 max-w-md">
          Share your Maarivil profile on Instagram, TikTok or WhatsApp — every fan you bring becomes a follower here, and a future booking.
        </p>
      </div>
      <div className="shrink-0">
        <ShareButton artist={artist} />
      </div>
    </div>
  );
};

export default FollowersWidget;
